import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Toolbar,
  Typography,
  useMediaQuery,
  Menu,
  MenuItem,
  Avatar,
  IconButton,
  Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Person,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { articleApi } from '../../services/saasApi';
import ThemeSwitcher from '../common/ThemeSwitcher';
import MobileNavigation from './MobileNavigation';
import Sidebar, { getSidebarWidth, readSidebarCollapsed, writeSidebarCollapsed } from './Sidebar';
import CommandPalette from './CommandPalette';

const MobileUserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton onClick={handleClick} size="small" aria-label="用户菜单">
        <Avatar
          src={
            user?.avatar_url &&
            (user.avatar_url.startsWith('http://') || user.avatar_url.startsWith('https://'))
              ? user.avatar_url
              : undefined
          }
          sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
        >
          {user?.username?.[0]?.toUpperCase() || <Person />}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{user?.username}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        {user?.is_admin && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/admin');
            }}
          >
            <AdminIcon fontSize="small" sx={{ mr: 1 }} />
            {t('nav.admin')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/settings');
          }}
        >
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          {t('nav.settings')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            logout();
            navigate('/login');
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          {t('auth.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [totalUnread, setTotalUnread] = useState(0);
  const [collapsed, setCollapsed] = useState<boolean>(readSidebarCollapsed);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await articleApi.getUnreadCounts(30);
      if (res.data.data) {
        setTotalUnread(res.data.data.total_unread);
      }
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (location.pathname.startsWith('/articles')) {
      fetchUnread();
    }
  }, [location.pathname, fetchUnread]);

  const handleToggleSidebar = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }, []);

  const sidebarWidth = getSidebarWidth(collapsed);

  return (
    <>
      {!isMobile && (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <Sidebar
            totalUnread={totalUnread}
            collapsed={collapsed}
            onToggleCollapsed={handleToggleSidebar}
          />
          <Box
            sx={{
              flex: 1,
              ml: sidebarWidth,
              transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
              minWidth: 0,
            }}
          >
            {/* 删除空 sticky header — 直接让 Outlet 顶到屏幕顶（Folo 风） */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Outlet />
            </Box>
          </Box>
        </Box>
      )}

      {isMobile && (
        <Container maxWidth="lg" disableGutters>
          <Box
            component="header"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: theme.zIndex.appBar,
              backgroundColor: 'var(--glass-bg-strong)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              borderBottom: '1px solid var(--glass-border)',
            }}
          >
            <Toolbar sx={{ minHeight: 56, px: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  flexGrow: 1,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/articles')}
              >
                PlotLake
              </Typography>
              <ThemeSwitcher />
              <Box sx={{ ml: 1 }}>
                <MobileUserMenu />
              </Box>
            </Toolbar>
          </Box>

          <Box sx={{ p: 2, pb: 10 }}>
            <Outlet />
          </Box>

          <MobileNavigation unreadCount={totalUnread} />
        </Container>
      )}

      {/* ⌘K Command Palette — 全局快捷面板 */}
      <CommandPalette />
    </>
  );
};

export default MainLayout;
